import React, { useEffect, useState } from "react";
import { BrowserRouter, Redirect } from "react-router-dom";
import { Provider } from "react-redux";

import {
	ErrorBoundary as SentryErrorBoundary,
	init as SentryInit
} from "@sentry/react";

import "@ant-design/compatible/assets/index.css";
import "./App.less";
import "./superops/styles/superops.less";

import store from "./redux-store";
import {
	cache,
	ZomentumStorageKeys,
	ZomentumWindow
} from "@/Contracts";

import { message } from "antd";
import { Auth0Provider } from "@auth0/auth0-react";
import { CaptureConsole, Dedupe } from "@sentry/integrations";

import AppDashboard from "./Components/UI/Dashboard";
import { errors } from "@/V2Utils/commonErrors";
import { Auth0LoginState } from "@/Contracts/Login";
import { beforeSendingEvent } from "./V2Utils/sentryConfig";
import ProposalBuilderWrapper from "@pages/ProposalBuilderWrapper";
import SalesActivityWrapper from "@pages/SalesActivity/SalesActivityWrapper";
import OnboardingWrapper from "@pages/OnboardingWrapper";
import { FlagsProviderComponent } from "@/Utils";
import { useEmbeddedMode, } from "./superops/hooks/useEmbeddedMode";
import { isAllowedIframe } from "./superops/utils/EmbedDetection";

declare let window: ZomentumWindow;

window.isLocalhost = Boolean(
	window.location.hostname === "localhost" ||
		// [::1] is the IPv6 localhost address.
		window.location.hostname === "[::1]" ||
		// 127.0.0.1/8 is considered localhost for IPv4.
		window.location.hostname.match(
			/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/
		)
);

window.isDevelopment =
	process.env.REACT_APP_NODE_ENV === "development" || window.isLocalhost;

window.isStaging =
	process.env.REACT_APP_NODE_ENV === "staging" || window.isLocalhost;

const ErrorFallback = () => {
	return <Redirect to="/error" />;
};

export interface Props {
	updateServiceWorker: (registration: ServiceWorkerRegistration) => void;
}

const App: React.FC<Props> = props => {
	// Initialize embedded mode functionality
	useEmbeddedMode();

	useEffect(() => {
		initializeSentry();
		message.config({ maxCount: 2 });
	}, []);

	const initializeSentry = () => {
		if (
			process.env.NODE_ENV === "production" &&
			process.env.REACT_APP_SENTRY
		) {
			try {
				SentryInit({
					enabled: true,
					dsn: process.env.REACT_APP_SENTRY,
					ignoreErrors: errors,
					beforeSend: beforeSendingEvent,
					release: process.env.REACT_APP_SHA,
					environment: process.env.REACT_APP_NODE_ENV,
					attachStacktrace: true,
					maxBreadcrumbs: 50,
					integrations: [
						new CaptureConsole({
							levels: ["error", "warn"]
						}),
						new Dedupe()
					],
					replaysSessionSampleRate: 1.0, // This sets the sample rate to be 10%. You may want this to be 100% while in development and sample at a lower rate in production
					replaysOnErrorSampleRate: 1.0 // If the entire session is not sampled, use this sample rate to sample sessions when an error occurs.
				});
			} catch (error) {
				console.error(error);
			}
		}
	};


	const [auth0LoginState, setAuth0LoginState] = useState<Auth0LoginState>({});

	if (!isAllowedIframe()) {
		return <h1>Sorry! This site cannot be operated in an iframe</h1>;
	} else {
		return (
			<Auth0Provider
				domain={process.env.REACT_APP_AUTH0_DOMAIN ?? ""}
				clientId={process.env.REACT_APP_AUTH0_CLIENT ?? ""}
				authorizationParams={{
					redirect_uri: `${window.location.origin}/authorize`,
					scope: "openid profile",
					audience: process.env.REACT_APP_AUTH0_AUDIENCE ?? ""
				}}
				onRedirectCallback={appState => {
					if (appState && appState?.return_to_url) {
						const returnToUrl = appState.return_to_url as
							| string
							| "";
						cache.save(
							ZomentumStorageKeys.RETURN_TO_URL,
							returnToUrl
						);
					}
					cache.save(ZomentumStorageKeys.APP_STATE, appState);
					return setAuth0LoginState(appState as Auth0LoginState);
				}}
			>
				<Provider store={store}>
					<BrowserRouter>
						<SentryErrorBoundary fallback={<ErrorFallback />}>
							<FlagsProviderComponent>
								<ProposalBuilderWrapper>
									<SalesActivityWrapper>
										<OnboardingWrapper>
											<AppDashboard
												updateServiceWorker={
													props.updateServiceWorker
												}
												auth0LoginState={
													auth0LoginState
												}
											/>
										</OnboardingWrapper>
									</SalesActivityWrapper>
								</ProposalBuilderWrapper>
							</FlagsProviderComponent>
						</SentryErrorBoundary>
					</BrowserRouter>
				</Provider>
			</Auth0Provider>
		);
	}
};

export default App;
